import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import { useLevels, activeLevelNames } from '@/hooks/useLevels';

interface Group {
  id: string;
  name: string;
  active?: boolean;
}

export interface StudentEdit {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  active: boolean;
  level: string | null;
  group_id: string | null;
  parent_name: string | null;
  parent_phone: string | null;
  notes: string | null;
}

interface StudentModalProps {
  open: boolean;
  onClose: () => void;
  student: StudentEdit | null;
}

export default function StudentModal({ open, onClose, student }: StudentModalProps) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const { levels } = useLevels();
  const levelOptions = activeLevelNames(levels);

  const isEdit = !!student;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function fetchGroups() {
      setLoadingGroups(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('zehrahoca_groups')
          .select('id, name, active')
          .eq('active', true)
          .order('created_at', { ascending: true });
        if (fetchError) throw fetchError;
        let list = (data ?? []) as Group[];
        // Düzenleme modunda: öğrencinin mevcut grubu pasifse onu da listeye ekle
        if (isEdit && student?.group_id) {
          const hasGroup = list.some((g) => g.id === student.group_id);
          if (!hasGroup) {
            const { data: gd, error: gErr } = await supabase
              .from('zehrahoca_groups')
              .select('id, name, active')
              .eq('id', student.group_id)
              .maybeSingle();
            if (!gErr && gd) list = [...list, gd as Group];
          }
        }
        if (!cancelled) setGroups(list);
      } catch {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    }

    fetchGroups();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setError(null);
      setSuccess(null);
      setSaving(false);
    }
  }, [open]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const full_name = (fd.get('full_name') as string || '').trim();
    const email = (fd.get('email') as string || '').trim().toLowerCase();
    const password = (fd.get('password') as string || '').trim();
    const phone = (fd.get('phone') as string || '').trim();
    const level = (fd.get('level') as string || '').trim();
    const group_id = (fd.get('group') as string || '').trim();
    const parent_name = (fd.get('parent_name') as string || '').trim();
    const parent_phone = (fd.get('parent_phone') as string || '').trim();
    const notes = (fd.get('notes') as string || '').trim();
    const active = fd.get('active') === 'on';

    if (!full_name) {
      setError('Lütfen öğrencinin adını ve soyadını girin.');
      return;
    }
    if (!isEdit && !email) {
      setError('Lütfen öğrenci için bir e-posta girin.');
      return;
    }
    if (!isEdit && password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (!group_id) {
      setError('Öğrenciyi en az bir gruba bağlamalısınız.');
      return;
    }

    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      if (isEdit) {
        const { error: profileError } = await supabase
          .from('zehrahoca_profiles')
          .update({ full_name, phone: phone || null, active })
          .eq('id', student!.id);
        if (profileError) throw profileError;

        const { error: studentError } = await supabase
          .from('zehrahoca_students')
          .update({
            level: level || null,
            group_id,
            parent_name: parent_name || null,
            parent_phone: parent_phone || null,
            notes: notes || null,
          })
          .eq('id', student!.id);
        if (studentError) throw studentError;

        form.reset();
        window.dispatchEvent(new Event('zehrahoca:students-changed'));
        window.dispatchEvent(new Event('zehrahoca:groups-changed'));
        onClose();
      } else {
        const { data, error: invokeError } = await supabase.functions.invoke('zehrahoca-create-student', {
          body: {
            full_name,
            email,
            password,
            phone: phone || null,
            level: level || null,
            group_id: group_id || null,
            parent_name: parent_name || null,
            parent_phone: parent_phone || null,
            notes: notes || null,
            active,
          },
        });

        if (invokeError) {
          throw new Error(invokeError.message || 'Sunucuyla iletişim kurulamadı. Lütfen tekrar deneyin.');
        }
        if (!data) {
          throw new Error('Sunucudan geçersiz yanıt alındı. Lütfen tekrar deneyin.');
        }
        if (data?.error) {
          throw new Error(data.error);
        }

        form.reset();
        window.dispatchEvent(new Event('zehrahoca:students-changed'));
        window.dispatchEvent(new Event('zehrahoca:groups-changed'));
        setSuccess('Öğrenci hesabı oluşturuldu. Öğrenci bu e-posta ve şifreyle giriş yapabilir.');
        onClose();
      }
    } catch (err) {
      setError((err as Error).message || 'Öğrenci kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Öğrenciyi Düzenle' : 'Yeni Öğrenci Ekle'}
      subtitle={isEdit ? 'Öğrenci bilgilerini güncelleyin.' : 'Öğrencinin giriş bilgilerini ve ders detaylarını girin.'}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {groups.length === 0 && !loadingGroups && (
          <div className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
            <p className="flex items-start gap-2 text-sm text-amber-800">
              <i className="ri-alert-line mt-0.5" />
              Öğrenci eklemeden önce en az bir grup oluşturmalısınız.
            </p>
            <button
              type="button"
              onClick={() => {
                onClose();
                navigate('/panel/gruplar');
              }}
              className="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-amber-600 cursor-pointer"
            >
              <i className="ri-team-line" /> Grup Oluştur
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ad Soyad</label>
            <input
              type="text"
              name="full_name"
              defaultValue={student?.full_name ?? ''}
              required
              placeholder="Örn: Elif Yılmaz"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">E-posta (giriş için)</label>
            <input
              type="email"
              name="email"
              defaultValue={student?.email ?? ''}
              disabled={isEdit}
              required={!isEdit}
              placeholder="ornek@mail.com"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 disabled:cursor-not-allowed disabled:bg-background-100 disabled:text-foreground-500"
            />
          </div>

          {!isEdit && (
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Şifre</label>
              <input
                type="text"
                name="password"
                required
                placeholder="En az 6 karakter"
                className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          )}

          {isEdit && (
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Telefon</label>
              <input
                type="tel"
                name="phone"
                defaultValue={student?.phone ?? ''}
                placeholder="05xx xxx xx xx"
                className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          )}

          {!isEdit && (
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Telefon</label>
              <input
                type="tel"
                name="phone"
                placeholder="05xx xxx xx xx"
                className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Seviye</label>
            <select
              name="level"
              defaultValue={student?.level ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Seviye seçin</option>
              {levelOptions.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
              Grup <span className="text-red-500">*</span>
            </label>
            <select
              name="group"
              defaultValue={student?.group_id ?? ''}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">{loadingGroups ? 'Yükleniyor...' : 'Grup seçin'}</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.active === false ? `${g.name} (Pasif)` : g.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Veli Adı</label>
            <input
              type="text"
              name="parent_name"
              defaultValue={student?.parent_name ?? ''}
              placeholder="Örn: Ayşe Yılmaz"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Veli Telefonu</label>
            <input
              type="tel"
              name="parent_phone"
              defaultValue={student?.parent_phone ?? ''}
              placeholder="05xx xxx xx xx"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Notlar</label>
            <textarea
              name="notes"
              defaultValue={student?.notes ?? ''}
              rows={2}
              maxLength={500}
              placeholder="Öğrenci hakkında notlar..."
              className="w-full resize-none rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 rounded-lg bg-background-50 p-4 text-sm text-foreground-800">
          <input
            type="checkbox"
            name="active"
            defaultChecked={student?.active ?? true}
            className="h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
          />
          Öğrenci aktif (panele giriş yapabilir)
        </label>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-3 text-sm text-emerald-700">
            <i className="ri-checkbox-circle-line mt-0.5 text-base" />
            <span>{success}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
          >
            {saving ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Öğrenciyi Ekle'}
          </button>
        </div>
      </form>
    </Modal>
  );
}